import React, { useCallback, useMemo } from 'react';
import { trackEvent } from '../../../../lib/analytics';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { showUndoToastAtom } from '../../../../atoms/v3/undoToastAtoms';
import {
  TaskItemProps,
  TaskCheckboxContainer,
  TaskTitle,
  TaskRecurrenceDisplay,
  TaskTimeInfo,
  TaskPriorityDisplay,
  TaskNotesDisplay,
  TaskItemShared
} from './base';



/**
 * iOS 风格的任务项组件
 */
const TaskItemComponent: React.FC<TaskItemProps> = ({
  task,
  onToggle,
  onClick,
  onProjectClick,
  isFirst = false,
  isLast = false,
  hideProjectName = false,
  context = 'normal' // 新增context参数，默认为normal
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);
  const [, showUndoToast] = useAtom(showUndoToastAtom);

  // 获取任务背景色 - 使用共享工具类，传递上下文
  const backgroundColorClass = useMemo(() => {
    return TaskItemShared.getBackgroundColorClass(context);
  }, [context]);

  // 获取复选框颜色 - 使用共享工具类
  const checkboxColorClass = useMemo(() => {
    return TaskItemShared.getCheckboxColorClass(task);
  }, [task.done, task.vital, task.urgent]);

  // 格式化日期 - 使用共享工具类
  const formatDate = useCallback(
    TaskItemShared.createFormatDate(t),
    [t]
  );

  // 格式化循环频率 - 使用共享工具类
  const formatRecurrenceFrequency = useCallback(
    TaskItemShared.createFormatRecurrenceFrequency(t, 'v3/tasks'),
    [t]
  );

  // 事件处理 - 使用共享工具类
  const { handleCheckboxClick, handleItemClick } = useMemo(() => {
    return TaskItemShared.createEventHandlers(
      task,
      onToggle,
      onClick,
      showUndoToast,
      trackEvent
    );
  }, [task, onToggle, onClick, showUndoToast]);

  // 获取任务项的边框样式 - 使用共享工具类
  const borderStyleClass = useMemo(() => {
    return TaskItemShared.getBorderStyleClass(isLast);
  }, [isLast]);

  return (
    <div
      className={`${borderStyleClass} ${backgroundColorClass}
      // animate-ios-fade-in
      hover:bg-black/[0.03] dark:hover:bg-white/[0.05] transition-colors duration-200 cursor-pointer`}
      onClick={handleItemClick}
    >
      <div className="flex items-start">
        {/* 左侧点击区域 - 整个左边都可以点击 */}
        <TaskCheckboxContainer
          task={task}
          onCheckboxClick={handleCheckboxClick}
          checkboxColorClass={checkboxColorClass}
          t={t}
          containerClassName="items-start justify-center pt-3.5"
        />

        {/* 任务内容 - 限制为两行 */}
        <div className="flex-1 overflow-hidden pr-4 py-3">
          {/* 第一行：任务标题 */}
          <TaskTitle
            task={task}
            hideProjectName={hideProjectName}
            onProjectClick={onProjectClick}
            t={t}
            titleClassName="text-ios-body truncate"
            titleColorClass=""
          />

          {/* 第二行：任务元数据和备注 - 只在有其他元信息时显示 */}
          {(task.vital || task.urgent || task.startAt || task.cost || task.dueAt || task.notes || task.recurrenceRule) && (
            <div className="flex items-center mt-0.5 text-ios-footnote text-ios-gray dark:text-ios-dark-gray truncate">
              {/* 优先级标签 - 使用精致的圆点样式 */}
              <TaskPriorityDisplay task={task} t={t} />

              {/* 循环任务标识 - 放在优先级标签后面 */}
              <TaskRecurrenceDisplay
                task={task}
                formatRecurrenceFrequency={formatRecurrenceFrequency}
              />

              {/* 时间信息 */}
              <TaskTimeInfo
                task={task}
                formatDate={formatDate}
                t={t}
                layout="separate"
              />

              {/* 备注预览 - 精致的样式 */}
              <TaskNotesDisplay task={task} />
            </div>
          )}
        </div>

        {/* 右侧箭头指示符 - iOS风格 */}
        <div className="mr-2 self-center text-ios-gray3 dark:text-ios-dark-gray3">
          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

// 使用 React.memo 包装组件，使用共享的比较函数
export const TaskItem = React.memo(TaskItemComponent, TaskItemShared.createMemoComparison());
